import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { formatNumeral, omit } from '@/utils/function'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getRevenueCombinedByCustomer } from '../../services'
import {
    RevenueAllocationsParams,
    RevenueCombined,
    RevenueCombinedByCustomer,
} from '../../types'
import { getCustomerGroupColumns } from './customer-group-columns'
import { getTableColumns } from './table-columns'

// Product group summary row for nested expandable
interface ProductGroupRow {
    product_group_name: string
    record_count: number
    total_amount: number
    items: RevenueCombined[]
}

// Group items by product_group_name, sorted by month within each group
const groupByProductGroup = (items: RevenueCombined[]): ProductGroupRow[] => {
    const grouped = new Map<string, RevenueCombined[]>()
    for (const item of items) {
        const key = item.product_group_name || '-'
        if (!grouped.has(key)) grouped.set(key, [])
        grouped.get(key)!.push(item)
    }

    return Array.from(grouped.entries())
        .map(([name, groupItems]) => ({
            product_group_name: name,
            record_count: groupItems.length,
            total_amount: groupItems.reduce(
                (sum, i) => sum + (i.allocated_amount || i.revenue_amount || 0),
                0
            ),
            items: groupItems.sort((a, b) =>
                (a.revenue_month || '').localeCompare(b.revenue_month || '')
            ),
        }))
        .sort((a, b) => b.total_amount - a.total_amount)
}

interface CustomerGroupTableProps {
    filters: RevenueAllocationsParams
    search?: string
}

const CustomerGroupTable: React.FC<CustomerGroupTableProps> = ({
    filters,
    search,
}) => {
    const { t } = useTranslation()
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(50)

    const {
        data: response,
        isLoading,
        isFetching,
    } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.REPORTS_BI_REVENUE_BY_CUSTOMER,
            filters,
            page,
            pageSize,
            search,
        ],
        queryFn: () =>
            getRevenueCombinedByCustomer({
                ...omit(filters, 'is_actual_revenue'),
                ...(filters?.is_actual_revenue ? { is_projected: false } : {}),
                page,
                page_size: pageSize,
                search: search || undefined,
                sort_by: 'total_amount',
                sort_order: 'desc',
            }),
        staleTime: 5 * 60 * 1000,
    })

    const data = response?.results || []
    const pagination = response?.pagination

    const columns = useMemo(() => getCustomerGroupColumns(t), [t])
    const detailColumns = useMemo(() => getTableColumns(t), [t])

    // Product group summary columns (level 2)
    const productGroupColumns = useMemo(
        () => [
            {
                key: 'product_group_name' as const,
                title: t('product_group_name', {
                    defaultValue: 'Nhóm dịch vụ',
                }),
                width: 320,
                render: (value: string) => (
                    <span className="text-xs font-medium text-gray-700">
                        {value || '-'}
                    </span>
                ),
            },
            {
                key: 'record_count' as const,
                title: t('record_count', { defaultValue: 'Số bản ghi' }),
                width: 120,
                headerClassName: 'text-right',
                cellClassName: 'text-right',
                render: (value: number) => (
                    <span className="text-xs text-gray-600">{value}</span>
                ),
            },
            {
                key: 'total_amount' as const,
                title: t('total_amount', { defaultValue: 'Tổng doanh thu' }),
                width: 180,
                headerClassName: 'text-right',
                cellClassName: 'text-right',
                render: (value: number) => (
                    <span className="text-xs font-medium text-gray-800">
                        {formatNumeral(value, 0)}
                    </span>
                ),
            },
        ],
        [t]
    )

    return (
        <DataTableCustomized<RevenueCombinedByCustomer>
            columns={columns}
            rows={data}
            loading={isLoading}
            isFetching={isFetching && !isLoading}
            pinnedHeader
            rowKey={(row) => String(row.customer_id)}
            resizable
            expandable={{
                expandedRowRender: (row) => {
                    const productGroups = groupByProductGroup(row.items || [])
                    return (
                        <div className="pl-4 py-1">
                            <DataTableCustomized<ProductGroupRow>
                                columns={productGroupColumns}
                                rows={productGroups}
                                rowKey={(r) => r.product_group_name}
                                expandable={{
                                    expandedColumns: detailColumns,
                                    expandedRowData: (r) => r.items,
                                    expandedRowKey: (item, index) =>
                                        `${item.invoice_id || 'projected'}-${item.revenue_month}-${index}`,
                                    rowExpandable: (r) => r.items.length > 0,
                                }}
                                emptyMessage={t('no_data')}
                            />
                        </div>
                    )
                },
                rowExpandable: (row) => (row.items?.length || 0) > 0,
            }}
            pagination={{
                page,
                pageSize,
                total: pagination?.total || 0,
                onPageChange: setPage,
                onPageSizeChange: (size) => {
                    setPageSize(size)
                    setPage(1)
                },
                pageSizeOptions: [20, 50, 100],
            }}
            emptyMessage={t('no_data')}
        />
    )
}

export default CustomerGroupTable
